/**
 * The two rules the magic link got wrong, kept where they can be tested.
 *
 * Measured on stage on 21/09 during the Serena end-to-end test: **four**
 * `POST /accounts/login` with the same token inside seven milliseconds, three answered 409
 * "token already used" and one 200. The 409 landed first, its handler cleared the query, and
 * the worker ended up on `/profilo` instead of the interview the link named.
 */

/** A router query as Next hands it over. */
type Query = Record<string, string | string[] | undefined>;

/**
 * `internalPath` and `destinationFromLocation` now live in
 * `libs/feature-authentication/src/utils/login-destination.ts`, which is the single owner of
 * the destination: `AuthGuard` needs the same rule on the **read** side, and a rule with two
 * copies is a rule with two behaviours. Re-exported here so the call sites and the tests in
 * this folder keep reading as they did.
 */
import { arrivalDestination, destinationFromLocation, internalPath } from '@restworld/feature-authentication';

export { internalPath, destinationFromLocation };

/**
 * Whether this token still has to be sent to the backend.
 *
 * The effect that verifies the link re-runs because its own dependencies change identity
 * during the login it is performing: `handleVerification` is rebuilt whenever `authContext`
 * moves, `removeFromUrl` whenever `router` does, and both move the moment the first response
 * lands. StrictMode's double invoke doubles it again in development.
 *
 * The comparison is on the token and not on a boolean, so a genuinely different link opened
 * in the same tab is still honoured.
 */
export const shouldVerifyToken = (token: string | undefined, lastVerified: string | null): boolean =>
  !!token && token !== lastVerified;

/**
 * What the query should become once the link has been spent.
 *
 * `token` always goes: it is a live credential and the address bar is the one place it must
 * not stay. **A `redirect_url` that names a place inside this app survives**, including when
 * the caller asks for every parameter to be dropped, and that is the whole point of this
 * function. Anything else is not a destination and does not survive.
 *
 * `redirect_url` is where the worker asked to go, and it is read *after* this runs, by
 * `AuthGuard` on the next render. Clearing it here is how the assessment link lost its
 * destination: the worker was signed in correctly, and by the time the guard looked for the
 * address it had just been erased.
 */
export const queryAfterLogin = (
  query: Query,
  { removeAllParams, destination }: { removeAllParams: boolean; destination?: string }
): Query | undefined => {
  // The router's copy first, the address bar's second. They agree whenever the router is
  // ready, and when it is not the second one is the only one there is.
  const kept = internalPath(query?.['redirect_url']) ?? destination;

  if (removeAllParams) {
    return kept ? { redirect_url: kept } : undefined;
  }

  const rest: Query = { ...query };
  delete rest['token'];

  // **The destination is rewritten here too, never copied through.** This branch used to
  // spread the router's query untouched, so a `redirect_url` pointing off the site survived
  // into the URL that `AuthGuard` then pushes. It was true before this PR and the bot review
  // is right that it is no less an open redirect for being old: the successful login is the
  // branch a worker actually takes.
  //
  // One rule for both branches now: whatever comes out has been through `internalPath`, and
  // a value that is not a place inside this app is dropped rather than carried.
  delete rest['redirect_url'];

  // `redirectTo` is the same thing under another name and `AuthGuard` pushes it just as
  // directly (`auth-guard.tsx`, the `/login` branch), so "one rule" has to mean this one too.
  // Raised by the Codex review as the half the first pass left open. The only caller that
  // sets it hands it `window.location.pathname`
  // (`legacy/job-favorite-position-modal/index.tsx`), so nothing legitimate is lost by
  // holding it to the same standard.
  const redirectTo = internalPath(query?.['redirectTo']);
  delete rest['redirectTo'];
  if (redirectTo) rest['redirectTo'] = redirectTo;

  // **The guard.** Never write back a query that has lost a destination the URL was carrying:
  // that single `replace` is what sent the worker to `/profilo`, and it is silent, because
  // losing a parameter looks exactly like never having had one. If the address bar has a
  // destination and the copy being written does not, the copy is wrong.
  if (kept) rest['redirect_url'] = kept;

  return rest;
};

/**
 * Tokens this tab has already sent, across mounts.
 *
 * The per-component ref covers the ordinary case, where the effect re-runs on the same
 * mount. It cannot cover a remount before the response lands: the ref is new and the token
 * is still in module state, so the second mount repeats the POST and earns a 409. Returns
 * `false` when the token has already been sent, so the caller does nothing.
 *
 * Deliberately never cleared. A token is spent once by definition, and the set holds at most
 * a handful of short strings for the life of the tab.
 */
const sentTokens = new Set<string>();

export const rememberVerifiedToken = (token: string): boolean => {
  if (sentTokens.has(token)) return false;
  sentTokens.add(token);
  return true;
};

/** Test seam: the set lives for the life of the module, which a test must be able to reset. */
export const forgetVerifiedTokens = (): void => sentTokens.clear();

/**
 * The query to write back after a login, destination included, from every source.
 *
 * This exists so that the **wiring** can be tested and not only its halves: `queryAfterLogin`
 * and the destination lookup were each covered while nothing asserted that the component put
 * them together, which is precisely the bolt that was loose in production — the pure function
 * was right and was being handed the wrong argument. `removeFromUrl` calls this and nothing
 * else.
 *
 * `arrivalDestination()` first, and this is what makes the rewrite safe rather than merely
 * careful: it is the value the address bar had when the page was **opened**, captured before
 * any of this ran, so no later `replace` — ours or Next's — can take it away. The live address
 * bar stays as a fallback for a visitor who reached `/login` some other way.
 */
export const queryAfterLoginFromUrl = (query: Query, { removeAllParams }: { removeAllParams: boolean }) =>
  queryAfterLogin(query, {
    removeAllParams,
    destination: arrivalDestination() ?? destinationFromLocation(),
  });
